import { AppState, Auth0Provider } from '@auth0/auth0-react'
import { ReactNode } from 'react'
import { useAppSelector, useAppDispatch } from 'app/state'
import { getLocalStorageItem, localStorageSupported, setLocalStorageItem } from 'app/api/localStorage'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { setJustLoggedIn, userLoginMetricsRequest } from 'features/users/state/usersSlice'
import { useEffect, useState } from 'react'
import settings from 'settings'
import useLoginHelper from './useLoginHelper'
import { isInIframe } from 'app/utils/embedding'
import useComesAuthenticatedFromAccounts from './useComesAuthenticatedFromAccounts'

const SsoDiscoveryRoute = 'sso-discovery'
const studentSearchParamKey = 'student'
interface Auth0ProviderWithUserProps {
  children: ReactNode
}

function Auth0ProviderWithUser({ children }: Auth0ProviderWithUserProps) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const [comesFromLogin, setComesFromLogin] = useState<boolean>(false)
  const { wasLoggedInAsStudent, markAsStudentLogin } = useLoginHelper()
  const comesAuthenticatedFromAccounts = useComesAuthenticatedFromAccounts()

  const isInStudentsLoginScreen = searchParams.has(studentSearchParamKey)
  const isLoginAsStudent = isInStudentsLoginScreen || wasLoggedInAsStudent

  const user = useAppSelector((state) => state.users.me)
  const { domain, audience, allScopes, accountsUrl } = settings
  const organizationId = getOrganizationId(searchParams)

  const clientId = isLoginAsStudent ? settings.studentClientId : settings.clientId

  const redirectCallback = (appState?: AppState) => {
    setComesFromLogin(true)
    dispatch(setJustLoggedIn(true))

    // If we're coming from auth0 login, we must remove  `code/state` from query string
    // to prevent 'invalid state' error, when/if user refreshes page.
    const returnUri = appState?.returnTo || window.location.pathname

    navigate(returnUri, { replace: true })
  }

  useEffect(() => {
    if (isInStudentsLoginScreen) {
      markAsStudentLogin()
    }
  }, [isInStudentsLoginScreen, markAsStudentLogin])

  useEffect(() => {
    if ((comesFromLogin || comesAuthenticatedFromAccounts) && user) {
      dispatch(userLoginMetricsRequest(user))
      setComesFromLogin(false)
    }
  }, [comesFromLogin, comesAuthenticatedFromAccounts, user, dispatch])

  const ssoDiscoveryUri = `${accountsUrl}/${SsoDiscoveryRoute}`
  const useLocalStorage = !isInIframe && localStorageSupported()

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      onRedirectCallback={redirectCallback}
      // NOTE: Using `memory` cache location and `useCookiesForTransactions` to avoid using `localStorage/sessionStorage` when they are not available
      cacheLocation={useLocalStorage ? 'localstorage' : 'memory'}
      {...(useLocalStorage ? {} : { useCookiesForTransactions: true })}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: audience,
        organization: organizationId,
        scope: allScopes,
        'ext-sso-discovery-uri': settings.isSnowflakeNativeApp ? undefined : ssoDiscoveryUri,
        'ext-standard-login-only': settings.isSnowflakeNativeApp
      }}
      legacySameSiteCookie={false}
    >
      {children}
    </Auth0Provider>
  )
}

function getOrganizationId(searchParams: URLSearchParams): string | undefined {
  // Allow normal login on self-hosted instances with SSO enabled
  if (settings.organizationId && searchParams.has('ssoDisabled')) {
    return
  }
  // Self-hosted instance settings will override the URL param for SSO
  if (settings.organizationId) {
    return settings.organizationId
  }

  const storedOrgId = localStorageSupported() ? getLocalStorageItem(settings.localStorageOrgIdKey) : null
  if (storedOrgId) {
    return storedOrgId
  }

  const organizationId = searchParams.get('sso')
  if (organizationId) {
    localStorageSupported() && setLocalStorageItem(settings.localStorageOrgIdKey, organizationId)
    return organizationId
  }
}

export default Auth0ProviderWithUser
